import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCog,
  faEdit,
  faEye,
  faPlus,
  faSearch,
  faTrash,
  faUser,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
  Col,
  Row,
  Form,
  Button,
  ButtonGroup,
  InputGroup,
  Card,
  Table,
  FormLabel,
  Modal,
  Badge,
  Alert,
} from "@themesberg/react-bootstrap";

import { useNavigate } from "react-router-dom";
import { fetchApi, postApi, getSector } from "../../../redux/api";
import { Routes } from "../../../routes";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { mainTreasuryModules } from "./modules";
import CustomTypeahead from "../../components/CustomTypeahead";

export function getDistinctObjects(array, key1, key2) {
  return array?.filter(
    (obj, index, self) =>
      index ===
      self?.findIndex((o) => o[key1] === obj[key1] && o[key2] === obj[key2])
  );
}

function UsersList() {
  const history = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const [userList, setUserList] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMDA, setSelectedMDA] = useState("");
  const [mdaList, setMdaList] = useState([]);
  const [budgetSummary, setBudgetSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newPassword, setNewPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const getUsers = () => {
    setLoading(true);
    fetchApi(`users`)
      .then((d) => {
        if (d && d.users) {
          setUserList(d.users);
          setFilteredUsers(d.users);
        }
      })
      .catch((e) => {
        console.log(e);
        toast.error("Failed to fetch users");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getUsers();
  }, []);

  // Fetch MDA list
  useEffect(() => {
    getSector({ type: "mdas" }, "mdas", (d) => {
      setBudgetSummary(d || []);
    });
  }, []);

  useEffect(() => {
    const filtered = userList.filter((user) => {
      const matchesSearch = user.fullname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.role?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesMDA = !selectedMDA || user.mda_name === selectedMDA;
      
      return matchesSearch && matchesMDA;
    });
    setFilteredUsers(filtered);
  }, [searchQuery, selectedMDA, userList]);

  useEffect(() => {
    // Update MDA list when userList changes
    const uniqueMDAs = [...new Set(userList.map(user => user.mda_name).filter(Boolean))];
    setMdaList(uniqueMDAs);
  }, [userList]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      id: user.id,
      fullname: user.fullname || user.name || "",
      username: user.username || "",
      email: user.email || "",
      role: user.role || "",
      phone: user.phone || "",
      status: user.status || "active",
      accessTo: user.accessTo ? user.accessTo.split(",").filter(Boolean) : [],
      functionalities: user.functionalities ? user.functionalities.split(",").filter(Boolean) : [],
      mda_code: user.mda_code || "",
      mda_name: user.mda_name || "",
    });
    setShowEditModal(true);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setShowResetPasswordModal(true);
  };

  const confirmResetPassword = () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setSubmitting(true);
    postApi(`admin_reset_password/${selectedUser.id}`, { newPassword })
      .then((response) => {
        if (response.success) {
          toast.success("Password reset successfully");
          setShowResetPasswordModal(false);
          setNewPassword("");
        } else {
          toast.error(response.message || "Failed to reset password");
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error("An error occurred while resetting password");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleStatusToggle = (user) => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    const action = newStatus === 'suspended' ? 'suspend' : 'activate';
    
    if (window.confirm(`Are you sure you want to ${action} ${user.fullname || user.name}?`)) {
      setSubmitting(true);
      postApi(`update_user_status/${user.id}`, { status: newStatus })
        .then((response) => {
          if (response.success) {
            toast.success(`User ${action}d successfully`);
            getUsers();
          } else {
            toast.error(`Failed to ${action} user`);
          }
        })
        .catch((error) => {
          console.error(error);
          toast.error(`An error occurred while ${action}ing user`);
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  const confirmDelete = () => {
    setSubmitting(true);
    fetchApi(`users/${selectedUser.id}`, { method: 'DELETE' })
      .then((response) => {
        if (response.success) {
          toast.success("User deleted successfully");
          getUsers();
          setShowDeleteModal(false);
        } else {
          toast.error("Failed to delete user");
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error("An error occurred while deleting user");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleEditSubmit = () => {
    setSubmitting(true);
    const updateData = {
      id: editForm.id,
      fullname: editForm.fullname,
      username: editForm.username,
      email: editForm.email,
      role: editForm.role,
      phone: editForm.phone,
      accessTo: editForm.accessTo.join(","),
      functionalities: editForm.functionalities.join(","),
      mda_name: editForm.mda_name,
      mda_code: editForm.mda_code,
    };

    postApi(`update_user-api-new/${editForm.id}`, updateData)
      .then((response) => {
        if (response.success) {
          toast.success("User updated successfully");
          getUsers();
          setShowEditModal(false);
        } else {
          toast.error(response.message || "Failed to update user");
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error("An error occurred while updating user");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleEditChange = ({ target: { name, value } }) => {
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const toggleAccess = (access) => {
    const title = typeof access === 'string' ? access : access.title;
    setEditForm(prev => {
      const newAccessTo = prev.accessTo.includes(title)
        ? prev.accessTo.filter(a => a !== title)
        : [...prev.accessTo, title];
      
      // If removing access, also remove all related functionalities
      let newFunctionalities = prev.functionalities;
      if (!newAccessTo.includes(title) && access.subMenu) {
        const subMenuTitles = access.subMenu.map(sub => sub.title);
        newFunctionalities = prev.functionalities.filter(f => !subMenuTitles.includes(f));
      }
      
      return {
        ...prev,
        accessTo: newAccessTo,
        functionalities: newFunctionalities
      };
    });
  };

  const toggleFunctionality = (functionality) => {
    const title = typeof functionality === 'string' ? functionality : functionality.title;
    setEditForm(prev => ({
      ...prev,
      functionalities: prev.functionalities.includes(title)
        ? prev.functionalities.filter(f => f !== title)
        : [...prev.functionalities, title]
    }));
  };

  const renderSubMenu = (subMenu) => {
    return subMenu.map((subItem, index) => (
      <div key={index} style={{ marginLeft: 20 }}>
        <Form.Check
          type="checkbox"
          label={subItem.title}
          checked={editForm.functionalities?.includes(subItem.title)}
          onChange={() => toggleFunctionality(subItem)}
        />
      </div>
    ));
  };

  const getStatusBadge = (status) => {
    const variant = status === 'active' ? 'success' : 
                   status === 'inactive' ? 'danger' : 'warning';
    return <Badge bg={variant}>{status || 'active'}</Badge>;
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>User Management</h4>
          <p className="mb-0">Manage system users and their permissions</p>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <Button
            className="me-2"
            onClick={() => history(`${Routes.userAdmin.editUser}`)}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" /> Create New User
          </Button>
        </div>
      </div>
      
      <Row className="mb-3">
        <Col md={4}>
          <FormLabel>Search Users</FormLabel>
          <InputGroup>
            <Form.Control
              placeholder="Search by name, username, email, or role"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <Button variant="outline-secondary">
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </InputGroup>
        </Col>
        <Col md={3}>
          <FormLabel>Filter by MDA</FormLabel>
          <Form.Select
            value={selectedMDA}
            onChange={(e) => setSelectedMDA(e.target.value)}
          >
            <option value="">All MDAs</option>
            {mdaList.map((mda, index) => (
              <option key={index} value={mda}>
                {mda}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={5} className="d-flex align-items-end justify-content-between">
          <div className="text-muted">
            Showing {filteredUsers.length} of {userList.length} users
            {selectedMDA && <span> • Filtered by: {selectedMDA}</span>}
          </div>
          {(searchQuery || selectedMDA) && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setSearchQuery("");
                setSelectedMDA("");
              }}
            >
              Clear Filters
            </Button>
          )}
        </Col>
      </Row>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <Alert variant="info">
              {searchQuery ? "No users found matching your search." : "No users found."}
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table striped hover>
                <thead className="table-dark">
                  <tr>
                    <th>S/N</th>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>MDA</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => (
                    <tr key={user.id || index}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
                          <strong>{user.fullname || user.name}</strong>
                        </div>
                      </td>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>
                        <Badge bg="primary">{user.role}</Badge>
                      </td>
                      <td>{user.mda_name || 'N/A'}</td>
                      <td>{getStatusBadge(user.status)}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleEdit(user)}
                            title="Edit user"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          {/* <Button
                            size="sm"
                            variant="outline-info"
                            onClick={() => history(`${Routes.userAdmin.editUser}/${user.id}`)}
                            title="Edit user profile"
                          >
                            <FontAwesomeIcon icon={faUser} />
                          </Button> */}
                          <Button
                            size="sm"
                            variant={user.status === 'active' ? 'outline-warning' : 'outline-success'}
                            onClick={() => handleStatusToggle(user)}
                            title={user.status === 'active' ? 'Suspend user' : 'Activate user'}
                          >
                            <FontAwesomeIcon icon={user.status === 'active' ? faTimes : faCheck} />
                          </Button>
                          {currentUser.role === 'admin' && (
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => handleResetPassword(user)}
                              title="Reset password"
                            >
                              <FontAwesomeIcon icon={faCog} />
                            </Button>
                          )}
                          {currentUser.role === 'admin' && user.id !== currentUser.id && (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDelete(user)}
                              title="Delete user"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Edit User: {selectedUser?.fullname || selectedUser?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="fullname"
                    value={editForm.fullname}
                    onChange={handleEditChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={editForm.username}
                    onChange={handleEditChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={editForm.email}
                    onChange={handleEditChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={editForm.phone}
                    onChange={handleEditChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    name="role"
                    value={editForm.role}
                    onChange={handleEditChange}
                  >
                    <option value="">Select Role</option>
                    <option value="Admin">Admin</option>
                    <option value="MDA ADMIN">MDA Admin</option>
                    <option value="USER">User</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <CustomTypeahead
                    labelKey="mda_name"
                    label="Select MDA"
                    options={getDistinctObjects(
                      budgetSummary?.map((i) => ({
                        mda_name: i.description,
                        mda_code: i.head,
                      })) || [],
                      "mda_name",
                      "mda_code"
                    )}
                    selected={editForm.mda_name ? [{ mda_name: editForm.mda_name, mda_code: editForm.mda_code }] : []}
                    onChange={(s) => {
                      if (s.length) {
                        setEditForm((p) => ({
                          ...p,
                          mda_code: s[0].mda_code,
                          mda_name: s[0].mda_name,
                        }));
                      } else {
                        setEditForm((p) => ({
                          ...p,
                          mda_code: "",
                          mda_name: "",
                        }));
                      }
                    }}
                    placeholder="Search and select MDA..."
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={editForm.status}
                    onChange={handleEditChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <h6 className="mb-3">Permissions and Access Level</h6>
            <Alert variant="info" className="mb-3">
              <small>Select the modules and functionalities this user should have access to.</small>
            </Alert>
            
            <Row>
              {mainTreasuryModules.map((item, index) => (
                <Col md="6" key={index} className="mb-3">
                  <Card className="h-100">
                    <Card.Body className="p-3">
                      <Form.Check
                        type="checkbox"
                        label={<strong>{item.title}</strong>}
                        checked={editForm.accessTo?.includes(item.title)}
                        onChange={() => toggleAccess(item)}
                        className="mb-2"
                      />
                      {editForm.accessTo?.includes(item.title) && item.subMenu && (
                        <div className="ms-3">
                          {renderSubMenu(item.subMenu)}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
              
              <Col md="6" className="mb-3">
                <Card className="h-100">
                  <Card.Body className="p-3">
                    <Form.Check
                      type="checkbox"
                      label={<strong>User Admin</strong>}
                      checked={editForm.accessTo?.includes("User Admin")}
                      onChange={() => toggleAccess("User Admin")}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleEditSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Updating...
              </>
            ) : (
              'Update User'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={showResetPasswordModal} onHide={() => setShowResetPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password: {selectedUser?.fullname || selectedUser?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (minimum 6 characters)"
                minLength={6}
              />
              <Form.Text className="text-muted">
                Password must be at least 6 characters long.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetPasswordModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={confirmResetPassword}
            disabled={submitting || !newPassword || newPassword.length < 6}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Are you sure you want to delete user <strong>{selectedUser?.fullname || selectedUser?.name}</strong>?
            This action cannot be undone.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDelete}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Deleting...
              </>
            ) : (
              'Delete User'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default UsersList;
